<template>
    <Transition name="mode-switch" mode="out-in">
        <Button
            v-if="isEditing"
            key="view"
            :variant="variant"
            :size="size"
            class="editing-button"
            :class="{ 'fixed-width': !variant }"
            aria-pressed="true"
            @click="stopEditing()"
            v-tooltip.left="{
                value: 'Return to view mode.',
                showDelay: 500,
            }"
        >
            <i class="pi pi-pencil" />
            <span>Editing</span>
            <!-- Trailing close glyph — the same "click to turn off" affordance as a
                 dismissible tag/chip — so the button reads as a toggle you can turn
                 off, not just a label describing the current mode. -->
            <i class="pi pi-times editing-button__close" />
        </Button>
        <Button
            v-else-if="!isViewOnly"
            key="edit"
            severity="secondary"
            :variant="variant"
            :size="size"
            :class="{ 'fixed-width': !variant }"
            icon="pi pi-pencil"
            :label="canEdit ? 'Edit' : `Syncing (${secondsLeft})`"
            :loading="!canEdit"
            aria-pressed="false"
            @click="beginEditing()"
            v-tooltip.left="{
                value: 'Start editing.',
                showDelay: 500,
            }"
        />
    </Transition>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";

import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { useHotKeys } from "@/composables/hotkeys";
import { useDraftEditing } from "@/composables/use-draft-editing";
import { syncChannel } from "@/lib/broadcast-channels";
import { INACTIVITY_TIMEOUT_MS } from "@/stores/sync-store";

const { addHotKey } = useHotKeys();
const { canEdit, isEditing, isViewOnly } = useEditManager();
const { stopEditing } = useEditStore();
const { beginEditing } = useDraftEditing();
const secondsLeft = ref(0);
const interval = ref<NodeJS.Timeout>();

addHotKey(
    "e",
    () => {
        if (!isEditing.value) {
            beginEditing();
        } else {
            stopEditing();
        }
    },
    "Switch to edit mode",
    "Global"
);

addHotKey(
    "Escape",
    () => {
        if (isEditing) {
            stopEditing();
        }
    },
    "Disable edit mode",
    "Global"
);

defineProps<{
    variant?: "outlined" | "text" | "link";
    size?: "small" | "large";
}>();

/**
 * Starts the countdown.
 */
const startCountdown = () => {
    secondsLeft.value = INACTIVITY_TIMEOUT_MS / 1000 + 1;
    interval.value = setInterval(() => {
        if (secondsLeft.value > 0) {
            secondsLeft.value -= 1;
        } else {
            clearInterval(interval.value);
        }
    }, 1000);
};

/**
 * (Re-)start the countdown on each new queue.
 */
syncChannel.addEventListener("message", ({ data: { message } }) => {
    if (message === "queued") {
        clearInterval(interval.value);
        startCountdown();
    }
});

/**
 * Clear countdown if you can edit again.
 */
watch(
    () => canEdit.value,
    (newValue) => {
        if (newValue) {
            clearInterval(interval.value);
        }
    }
);
</script>

<style lang="scss" scoped>
// Filled/colored so the current mode reads at a glance, rather than only the
// label changing between "Edit" and "View" — same "active toggle" treatment
// as the Drafts button in ApplicationLayout.vue.
.editing-button {
    background-color: var(--stekz-primary-background);
    border-color: var(--stekz-primary-background);
    color: var(--stekz-primary-color);
    font-weight: 600;

    &__close {
        font-size: 0.8em;
        opacity: 0.75;
    }

    &:hover &__close {
        opacity: 1;
    }
}
.p-button {
    white-space: nowrap;
}

// Reserves enough width for the widest state ("Syncing (NN)") so switching
// between Edit/Editing/Syncing doesn't shift the rest of the toolbar around.
// Only applied to the toolbar's own (variant-less) usage — the inline
// variant="link" usages elsewhere size to their surrounding text as before.
.fixed-width {
    min-width: 8rem;
    justify-content: center;
}

.mode-switch-enter-active,
.mode-switch-leave-active {
    transition:
        opacity 120ms ease,
        transform 120ms ease;
}
.mode-switch-enter-from {
    opacity: 0;
    transform: scale(0.9);
}
.mode-switch-leave-to {
    opacity: 0;
    transform: scale(0.9);
}
</style>
